import React from 'react';
import useSWR from 'swr';
import View from '../view';
import { Link } from '@/navigation';
import { Content } from '@/typings/projects';
import ClientFetch from '@/server/fetch/client';
import { LocaleProps } from '@/typings/next-page';
import { Button, Chip, Image } from '@nextui-org/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Modal, ModalContent, ModalBody } from '@nextui-org/react';

type Props = {
  projectId: string | null;
  onClose: () => void;
} & LocaleProps;

const MarkerViewCard: React.FC<Props> = ({ projectId, params, onClose }) => {
  const { data, error, isLoading } = useSWR<Content>(
    projectId
      ? `/projects/${projectId}/map-view?locale=${params?.locale}`
      : null,
    (url: string) => ClientFetch.get(url).then((data) => data.data)
  );

  return (
    <Modal
      disableAnimation
      isOpen={Boolean(projectId)}
      size="4xl"
      onClose={onClose}
    >
      <ModalContent>
        {() => (
          <>
            <View className="flex-1 flex items-center justify-between px-2">
              <p className="text-default-700 text-small">Map View</p>
              <Button
                isIconOnly
                size="sm"
                radius="full"
                variant="light"
                onPress={onClose}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </View>
            {error ? (
              <ModalBody className="flex flex-col items-center justify-center p-2 min-h-96 gap-2">
                <Chip size="sm" color="danger">
                  Error fetching data. Please try again later.
                </Chip>
                <Button size="sm" variant="flat" onPress={onClose}>
                  Close
                </Button>
              </ModalBody>
            ) : (
              <ModalBody className="flex flex-col p-2 min-h-96">
                {isLoading ? (
                  <View className="flex-1 flex flex-col sm:flex-row gap-4 animate-pulse">
                    <View className="flex-[0.65] flex flex-col bg-gray-300 rounded-xl">
                      <div className="w-full h-0 aspect-w-16 aspect-h-9"></div>
                    </View>
                    <View className="flex-[0.50] flex flex-col gap-2">
                      <View className="flex items-center justify-between">
                        <span className="w-full min-h-4 bg-gray-300 rounded-xl"></span>
                      </View>
                      <div className="text-base text-gray-400 line-clamp-6">
                        {Array.from({ length: 7 }).map((_, index) => (
                          <div
                            key={index}
                            className="h-2 bg-gray-300 rounded-md mb-2"
                            style={{ width: `${Math.random() * 90 + 10}%` }}
                          ></div>
                        ))}
                      </div>
                      <View className="grid grid-cols-3 gap-1">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div
                            key={i}
                            className="h-28 bg-gray-300 rounded-md"
                          ></div>
                        ))}
                      </View>
                    </View>
                  </View>
                ) : (
                  <View className="flex-1 flex gap-4 flex-col sm:flex-row">
                    <View className="flex-[0.65] flex flex-col">
                      <React.Suspense fallback={null}>
                        <iframe
                          src={data?.location?.map || ''}
                          width="100%"
                          height="100%"
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          className="flex-1 w-full h-full rounded-xl border-none outline-none aspect-w-16 aspect-h-9"
                        />
                      </React.Suspense>
                    </View>
                    <View className="flex-[0.50] flex flex-col gap-2">
                      <View className="flex items-center justify-between">
                        <h2 className="text-2xl text-default-900 font-normal urw-din-medium line-clamp-3">
                          {data?.title}
                        </h2>
                      </View>

                      <View className="flex">
                        <p className="text-base text-default-600 line-clamp-6">
                          {data?.description}
                        </p>
                      </View>
                      <View className="flex justify-between items-center gap-1">
                        <View className="flex gap-1">
                          <Chip
                            size="sm"
                            color="default"
                            variant="bordered"
                            radius="sm"
                            className="text-default-400"
                          >
                            Image ({data?.gallery?.length})
                          </Chip>
                        </View>
                        <View className="flex gap-1">
                          <Link
                            href={{
                              pathname: `/projects/[slug]`,
                              params: {
                                slug: data?.slug as string,
                              },
                            }}
                          >
                            <Chip
                              radius="sm"
                              size="sm"
                              color="primary"
                              variant="flat"
                            >
                              Read More
                            </Chip>
                          </Link>
                        </View>
                      </View>
                      <View className="grid grid-cols-3 gap-1">
                        {data?.gallery?.map(({ alt, small }, i) => (
                          <Image
                            radius="sm"
                            key={i}
                            alt={alt}
                            src={small}
                            removeWrapper
                            className="h-24 w-full object-cover max-w-[unset]"
                          />
                        ))}
                      </View>
                    </View>
                  </View>
                )}
              </ModalBody>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default MarkerViewCard;
